// relTime + SLA-chip + small display helpers — thresholds ported verbatim
// from the design spec pack §4 (behavior spec) / prototype `relTime`,
// `slaChip`, `pretty`, `*Kind` helpers.

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;

export function relTime(iso: string | number | Date): string {
  const ts = iso instanceof Date ? iso.getTime() : typeof iso === 'number' ? iso : new Date(iso).getTime();
  const s = Math.max(0, Date.now() - ts);
  if (s < MINUTE) return 'just now';
  if (s < HOUR) return `${Math.floor(s / MINUTE)}m ago`;
  if (s < DAY) return `${Math.floor(s / HOUR)}h ago`;
  if (s < DAY * 30) return `${Math.floor(s / DAY)}d ago`;
  return `${Math.floor(s / (DAY * 30))}mo ago`;
}

// "backlog" -> "Backlog", "in-progress" -> "In progress"
export function pretty(s: string | null | undefined): string {
  if (!s) return '';
  return s.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

export type ChipKind = 'lime' | 'teal' | 'warn' | 'danger' | 'gray';

export function ticketKind(status: string): ChipKind {
  return (
    (
      {
        open: 'teal',
        triaged: 'warn',
        'in-progress': 'teal',
        resolved: 'lime',
        closed: 'gray'
      } as Record<string, ChipKind>
    )[status] ?? 'gray'
  );
}

export function projKind(status: string): ChipKind {
  return (({ active: 'lime', paused: 'gray', archived: 'gray' }) as Record<string, ChipKind>)[status] ?? 'gray';
}

// Team chip color mapping (see $lib/types.ts#Team / genba-api-spec.md's
// Teams section) — picked from the existing 5-color chip palette so teams
// read as visually distinct from every other chip on the same pages (role,
// project status, ticket SLA, debt severity): fullstack=teal, embedded=lime,
// networking=gray. 'warn'/'danger' are left alone since those already carry
// an urgency connotation elsewhere that teams shouldn't borrow.
const TEAM_KINDS: Record<string, ChipKind> = {
  fullstack: 'teal',
  embedded: 'lime',
  networking: 'gray'
};
export function teamKind(team: string | null | undefined): ChipKind {
  return team ? (TEAM_KINDS[team] ?? 'gray') : 'gray';
}
export function teamLabel(team: string | null | undefined): string {
  return team ? pretty(team) : '—';
}

export function sevKind(sev: string): ChipKind {
  return (({ low: 'gray', medium: 'warn', high: 'danger' }) as Record<string, ChipKind>)[sev] ?? 'gray';
}

export function ticketAgeDays(createdAt: string): number {
  return Math.floor((Date.now() - new Date(createdAt).getTime()) / DAY);
}

export function ticketOpen(status: string): boolean {
  return status !== 'resolved' && status !== 'closed';
}

// Open tickets only — hidden (null) for resolved/closed, per behavior spec:
// >=4d -> danger "Xd · overdue"; >=2d -> warn "Xd open"; else gray
// ("today" at 0d, else "Xd open").
export function slaChip(status: string, createdAt: string): { label: string; kind: ChipKind } | null {
  if (!ticketOpen(status)) return null;
  const days = ticketAgeDays(createdAt);
  if (days >= 4) return { label: `${days}d · overdue`, kind: 'danger' };
  if (days >= 2) return { label: `${days}d open`, kind: 'warn' };
  return { label: days > 0 ? `${days}d open` : 'today', kind: 'gray' };
}

/* ============================================================
   IST day helpers (admin daily-activity view)

   Genba's server cuts "a day" in Asia/Kolkata — see
   internal/api/handlers/admin_activity.go. The browser must agree, or the
   ◀/▶ arrows and the "Today" button would walk a different calendar than
   the data does whenever the viewer's machine is on another zone. Every
   day string below is the API's `YYYY-MM-DD` form; every arithmetic step
   goes through UTC so it can't drift on a DST-observing local zone.
   ============================================================ */
export const IST_TZ = 'Asia/Kolkata';

// "2026-08-02" for an instant as it reads in IST. en-CA is the ISO-ordered
// locale, which is exactly the shape the API's ?date= expects.
export function istDay(d: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(d);
}

// "14:05" — an activity's wall-clock time in the office's zone, never the
// viewer's.
export function istTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', {
    timeZone: IST_TZ,
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function isValidDay(day: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(day) && !Number.isNaN(Date.parse(`${day}T00:00:00Z`));
}

// Walk a day string by ±n calendar days. Parsed and formatted in UTC so the
// result is pure string arithmetic on the IST calendar, independent of where
// the browser is.
export function shiftDay(day: string, delta: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}

// "Sun, 2 Aug 2026" — formatted in UTC on purpose: `day` is already an IST
// calendar date, so re-zoning it would shift it by a day near midnight.
export function dayLabel(day: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(new Date(`${day}T00:00:00Z`));
}

// Activity `type` values are snake_case and domain-prefixed (task_create,
// task_move, ticket_triage, deploy, debt_add, doc_update), so color by the
// domain rather than enumerating every verb — a new verb inherits its
// domain's chip instead of silently falling back to gray.
const ACTIVITY_KINDS: Record<string, ChipKind> = {
  task: 'teal',
  ticket: 'warn',
  deploy: 'lime',
  debt: 'danger',
  doc: 'gray'
};
export function activityKind(type: string): ChipKind {
  return ACTIVITY_KINDS[type.split('_')[0]] ?? 'gray';
}

// There is deliberately no per-type phrase table here any more: the server
// writes a self-describing sentence into Activity.text ("Moved WHP-101 to
// Done: Wire up guest-room scene presets"), so restating the type as
// "Moved task" only pushed the useful half of the row out of view.

// Plural domain labels for the per-person summary chips ("Tasks 5").
const ACTIVITY_DOMAIN_LABELS: Record<string, string> = {
  task: 'Tasks',
  ticket: 'Tickets',
  deploy: 'Deploys',
  debt: 'Debt',
  doc: 'Docs'
};
export function activityDomainLabel(type: string): string {
  return ACTIVITY_DOMAIN_LABELS[type.split('_')[0]] ?? pretty(type);
}

// Deterministic "site code" fallback when a ticket has no src_site — same
// hash as the prototype's siteCode() so re-imported demo data would match.
export function siteCode(site: string | undefined, company: string | undefined, property: string | undefined): string {
  if (site) return site;
  const s = `${company || ''}|${property || ''}`;
  let n = 0;
  for (let i = 0; i < s.length; i++) n = (n * 31 + s.charCodeAt(i)) >>> 0;
  return `STE-${n.toString(36).toUpperCase().slice(0, 4)}`;
}
